import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import PulseLoader from 'react-spinners/PulseLoader';
import classNames from 'classnames';

import useJiraTasks from '../../../hooks/useJiraTasks';
import { importJiraTasks } from '../../../services/jira';
import { MultiSelectDropdown } from '@/components/MultiSelectDropdown';
import { InputComponent } from '@/components/Input/Input.component';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { appRoutes } from '@/routes/routes';
import JiraTaskDetailDrawer from './JiraTaskDetailDrawer.component';
import styles from './JiraTasksTab.module.scss';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTimeAgo = (date) => {
  if (!date) return null;
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const formatDueDate = (value) => {
  if (!value) return '—';
  const date = value?.toDate ? value.toDate() : new Date(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusClass = (statusCategory) => {
  if (statusCategory === 'done') return styles.done;
  if (statusCategory === 'in-progress' || statusCategory === 'indeterminate')
    return styles.inProgress;
  return styles.todo;
};

const getTypeClass = (issueType) => {
  const t = issueType?.toLowerCase();
  if (t === 'bug') return styles.bug;
  if (t === 'story') return styles.story;
  if (t === 'task') return styles.task;
  if (t === 'epic') return styles.epic;
  return '';
};

const deriveOptions = (tasks, keyFn, labelFn) => {
  const seen = new Set();
  const options = [];
  for (const task of tasks) {
    const key = keyFn(task);
    if (key && !seen.has(key)) {
      seen.add(key);
      options.push({ value: key, label: labelFn(task) });
    }
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

// ─── Cell renderers ───────────────────────────────────────────────────────────

const IndexCell = (_, column) => (
  <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Medium}>
    {column.rowIndex + 1}
  </Text>
);

const KeyCell = (rowData) => (
  <span className={styles.issueKey}>{rowData.key}</span>
);

const SummaryCell = (rowData) => (
  <div className={styles.summaryCell}>
    <span className={styles.summaryText} title={rowData.summary}>
      {rowData.summary}
    </span>
  </div>
);

const TypeCell = (rowData) => (
  <span className={classNames(styles.typeTag, getTypeClass(rowData.issueType))}>
    {rowData.issueType || '—'}
  </span>
);

const StatusCell = (rowData) => (
  <span
    className={classNames(
      styles.statusTag,
      getStatusClass(rowData.statusCategory)
    )}
  >
    {rowData.status || '—'}
  </span>
);

const AssigneeCell = (rowData) => (
  <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Medium}>
    {rowData.assigneeName || '—'}
  </Text>
);

const DueDateCell = (rowData) => (
  <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Medium}>
    {formatDueDate(rowData.dueDate)}
  </Text>
);

// ─── Sync state machine ───────────────────────────────────────────────────────
// idle | syncing | success | error | cooldown
const SYNC_COOLDOWN_MS = 60_000;

const useSyncButton = (companyId, lastImportAt, onSyncComplete) => {
  const [syncState, setSyncState] = useState('idle');
  const [syncError, setSyncError] = useState(null);
  const [, setTick] = useState(0);
  const successTimerRef = useRef(null);
  const tickIntervalRef = useRef(null);

  // Derive cooldown from lastImportAt (Firestore-backed — works across tabs/users)
  const secondsSinceLastImport = lastImportAt
    ? Math.floor((Date.now() - lastImportAt.getTime()) / 1000)
    : null;
  const isInCooldown =
    syncState !== 'syncing' &&
    secondsSinceLastImport !== null &&
    secondsSinceLastImport < SYNC_COOLDOWN_MS / 1000;

  // Tick every second while in cooldown so the countdown re-renders
  useEffect(() => {
    if (isInCooldown) {
      tickIntervalRef.current = setInterval(() => setTick((n) => n + 1), 1000);
    } else {
      clearInterval(tickIntervalRef.current);
    }
    return () => clearInterval(tickIntervalRef.current);
  }, [isInCooldown]);

  useEffect(() => {
    const successTimer = successTimerRef.current;
    return () => clearTimeout(successTimer);
  }, []);

  const sync = useCallback(async () => {
    if (syncState === 'syncing' || isInCooldown) return;

    setSyncState('syncing');
    setSyncError(null);

    try {
      const result = await importJiraTasks(companyId);
      setSyncState('success');
      onSyncComplete({ success: true, result });

      successTimerRef.current = setTimeout(() => setSyncState('idle'), 3000);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Sync failed';
      setSyncError(msg);
      setSyncState('error');
    }
  }, [companyId, syncState, isInCooldown, onSyncComplete]);

  return { sync, syncState, syncError, isInCooldown, secondsSinceLastImport };
};

// ─── Component ────────────────────────────────────────────────────────────────

const JiraTasksTab = ({ companyId, jiraStatus }) => {
  const history = useHistory();
  const toastRef = useRef(null);
  const {
    tasks: allTasks,
    loading,
    error,
    refetch,
  } = useJiraTasks(companyId, jiraStatus.connected);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [tableFirst, setTableFirst] = useState(0);
  const [tableRows, setTableRows] = useState(50);

  const handleSyncComplete = useCallback(
    ({ success, result }) => {
      if (!success) return;
      refetch();
      const { imported = 0, errors = 0 } = result ?? {};
      const detail =
        errors > 0
          ? `${imported} task${imported !== 1 ? 's' : ''} synced, ${errors} error${errors !== 1 ? 's' : ''}`
          : imported === 0
            ? 'Already up to date'
            : `${imported} task${imported !== 1 ? 's' : ''} synced`;
      toastRef.current?.show({
        severity: 'success',
        summary: 'Jira Synced',
        detail,
        life: 4000,
      });
    },
    [refetch]
  );

  const { sync, syncState, syncError, isInCooldown, secondsSinceLastImport } =
    useSyncButton(companyId, jiraStatus.lastImportAt, handleSyncComplete);

  // Surface sync errors as toast
  useEffect(() => {
    if (syncState !== 'error' || !syncError) return;
    let summary = 'Sync failed';
    let detail = syncError;
    if (/no projects selected/i.test(syncError)) {
      summary = 'No projects selected';
      detail = 'Select Jira projects in Integrations first.';
    } else if (/not connected/i.test(syncError)) {
      summary = 'Jira not connected';
      detail = 'Reconnect Jira in Integrations.';
    } else if (/401|token|expired/i.test(syncError)) {
      summary = 'Jira session expired';
      detail = 'Please reconnect your Jira account in Integrations.';
    } else if (/429|rate/i.test(syncError)) {
      summary = 'Rate limited by Jira';
      detail = 'Too many requests. Try again in a few minutes.';
    }
    toastRef.current?.show({ severity: 'error', summary, detail, life: 6000 });
  }, [syncState, syncError]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setTableFirst(0);
  }, [searchTerm, selectedProjects, selectedStatuses, selectedTypes]);

  // Derive filter options lazily from the loaded task set
  const projectOptions = useMemo(
    () =>
      deriveOptions(
        allTasks,
        (t) => t.projectKey,
        (t) => t.projectName || t.projectKey
      ),
    [allTasks]
  );
  const statusOptions = useMemo(
    () =>
      deriveOptions(
        allTasks,
        (t) => t.status,
        (t) => t.status
      ),
    [allTasks]
  );
  const typeOptions = useMemo(
    () =>
      deriveOptions(
        allTasks,
        (t) => t.issueType,
        (t) => t.issueType
      ),
    [allTasks]
  );

  const filteredTasks = useMemo(() => {
    let result = allTasks;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.summary?.toLowerCase().includes(term) ||
          t.key?.toLowerCase().includes(term)
      );
    }

    if (selectedProjects.length > 0) {
      const keys = new Set(selectedProjects.map((p) => p.value));
      result = result.filter((t) => keys.has(t.projectKey));
    }

    if (selectedStatuses.length > 0) {
      const statuses = new Set(selectedStatuses.map((s) => s.value));
      result = result.filter((t) => statuses.has(t.status));
    }

    if (selectedTypes.length > 0) {
      const types = new Set(selectedTypes.map((ty) => ty.value));
      result = result.filter((t) => types.has(t.issueType));
    }

    return result;
  }, [allTasks, searchTerm, selectedProjects, selectedStatuses, selectedTypes]);

  const handleRowClick = useCallback((e) => setActiveTask(e.data), []);
  const handleCloseDrawer = useCallback(() => setActiveTask(null), []);

  const syncButtonIcon =
    syncState === 'syncing'
      ? null
      : syncState === 'success'
        ? 'pi pi-check'
        : syncState === 'error'
          ? 'pi pi-exclamation-triangle'
          : 'pi pi-refresh';

  const syncButtonLabel =
    syncState === 'syncing'
      ? 'Syncing…'
      : syncState === 'success'
        ? 'Synced'
        : syncState === 'error'
          ? 'Retry Sync'
          : isInCooldown
            ? `Sync (${Math.max(0, SYNC_COOLDOWN_MS / 1000 - secondsSinceLastImport)}s)`
            : 'Sync';

  const lastImportedLabel = jiraStatus.lastImportAt
    ? `Last synced ${formatTimeAgo(jiraStatus.lastImportAt)}`
    : null;

  // ── Error state ──
  if (error) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyTitle}>Failed to load Jira tasks</span>
        <span className={styles.emptySubtitle}>
          Check your connection and try again.
        </span>
        <Button
          label="Retry"
          icon="pi pi-refresh"
          className="p-button-text"
          onClick={refetch}
        />
      </div>
    );
  }

  // ── Never imported ──
  if (!loading && allTasks.length === 0 && !jiraStatus.lastImportAt) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyIcon}>📋</span>
        <span className={styles.emptyTitle}>No Jira tasks imported yet</span>
        <span className={styles.emptySubtitle}>
          Go to Integrations → Jira to select projects and run your first
          import.
        </span>
        <button
          className={styles.goToIntegrationsLink}
          onClick={() => history.push(appRoutes().integrationsJira)}
        >
          Go to Jira Integration →
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Toast ref={toastRef} position="top-right" />
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <MultiSelectDropdown
          label="Project"
          options={projectOptions}
          value={selectedProjects}
          onChange={setSelectedProjects}
        />
        <MultiSelectDropdown
          label="Status"
          options={statusOptions}
          value={selectedStatuses}
          onChange={setSelectedStatuses}
        />
        <MultiSelectDropdown
          label="Type"
          options={typeOptions}
          value={selectedTypes}
          onChange={setSelectedTypes}
        />
      </div>

      {/* Table card */}
      <div className={styles.taskListContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableHeaderLeft}>
            <InputComponent
              placeholder="Search by key or title"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.tableHeaderRight}>
            {lastImportedLabel && (
              <span className={styles.lastImportedText}>
                {lastImportedLabel}
              </span>
            )}
            <Button
              icon={syncButtonIcon}
              label={syncButtonLabel}
              className={classNames('p-button-text', styles.syncBtn, {
                [styles.syncSuccess]: syncState === 'success',
                [styles.syncError]: syncState === 'error',
              })}
              loading={syncState === 'syncing'}
              disabled={syncState === 'syncing' || isInCooldown}
              onClick={sync}
              tooltip={
                isInCooldown
                  ? `Cooldown: wait ${Math.max(0, SYNC_COOLDOWN_MS / 1000 - secondsSinceLastImport)}s`
                  : syncState === 'error'
                    ? syncError
                    : 'Sync with Jira'
              }
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>

        {loading ? (
          <div className="w-100 d-flex justify-content-center p-4">
            <PulseLoader color="#0052cc" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyTitle}>
              No tasks match your filters
            </span>
            <span className={styles.emptySubtitle}>
              Try clearing your filters or running a new import.
            </span>
          </div>
        ) : (
          <DataTable
            className="prime-react-table"
            responsiveLayout="scroll"
            value={filteredTasks}
            paginator
            paginatorClassName="prime-react-paginator"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            style={{ cursor: 'pointer' }}
            first={tableFirst}
            rows={tableRows}
            rowsPerPageOptions={[10, 20, 50]}
            onPage={(e) => {
              setTableFirst(e.first);
              setTableRows(e.rows);
            }}
            onRowClick={handleRowClick}
          >
            <Column
              field="#"
              header="Sr.No."
              body={IndexCell}
              className="first-col td-start"
            />
            <Column
              field="key"
              header="Key"
              body={KeyCell}
              style={{ minWidth: '90px' }}
            />
            <Column
              field="summary"
              header="Summary"
              body={SummaryCell}
              className="second-col"
              sortable
            />
            <Column
              field="issueType"
              header="Type"
              body={TypeCell}
              style={{ minWidth: '90px' }}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={StatusCell}
              style={{ minWidth: '120px' }}
              sortable
            />
            <Column
              field="priority"
              header="Priority"
              className="first-col td-start"
              sortable
            />
            <Column
              field="assigneeName"
              header="Assignee"
              body={AssigneeCell}
              className="fifth-col td-start"
              sortable
            />
            <Column
              field="dueDate"
              header="Due Date"
              body={DueDateCell}
              className="fifth-col td-start"
              sortable
            />
          </DataTable>
        )}
      </div>

      {/* Detail drawer — portal-style overlay */}
      {activeTask && (
        <JiraTaskDetailDrawer
          task={activeTask}
          onClose={handleCloseDrawer}
          siteUrl={jiraStatus.siteUrl}
        />
      )}
    </div>
  );
};

export default JiraTasksTab;
